import * as React from "react";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@components/ui/field";
import { Input } from "@components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import {
  MENTION_SOURCE_TYPE,
  SEARCH_TYPES,
  SEARCH_TYPE_TO_LABEL,
  SearchTypes,
} from "@/constants";
import DatePicker from "@components/ui/date-picker";
import { Button } from "@components/ui/button";
import { PlusIcon } from "lucide-react";
import { addMonths, endOfDay, isBefore, startOfDay, subMonths } from "date-fns";
import { Trans, useLingui } from "@lingui/react/macro";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Organization } from "@/types/Organization";
import { getCapabilities } from "@/api/endpoints/organization";
import Query from "./partials/Query";
import {
  FilterKey,
  prepareQueryForCreate,
  PROP_TO_PARAM_MAP,
  QueryIn,
} from "../QueryBuilder/requestUtils";
import { QueryBuilderForm, useQueryBuilderFormSchema } from "./types";
import { i18n } from "@lingui/core";
import { getTags } from "@/api/endpoints/tags";
import Staff from "./partials/Staff";
import { SearchQuery } from "@/types/SearchQuery";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import { toast } from "sonner";

export enum QueryBuilderSubmitButton {
  Create = "form-qb-create",
  Preview = "form-qb-preview",
}

type QueryBuilderProps = {
  onSubmit: (
    values: QueryBuilderForm,
    submitterName?: QueryBuilderSubmitButton
  ) => void;
};

export default function QueryBuilder({ onSubmit }: QueryBuilderProps) {
  const { t } = useLingui();

  const queryBuilderFormSchema = useQueryBuilderFormSchema();

  // URL state
  const { orgUuid } = useParams<{ orgUuid: Organization["uuid"] }>();

  // Refs
  const hasInitializedForm = React.useRef<boolean>(false);

  // Server state
  const { data: capabilitiesData } = useQuery({
    queryFn: () => getCapabilities(orgUuid),
    queryKey: [orgUuid, "capabilities"],
    enabled: Boolean(orgUuid),
  });

  const { data: tagsData } = useQuery({
    queryKey: [orgUuid, "tags"],
    queryFn: () => getTags(),
  });

  // Helpers
  const capabilities = capabilitiesData?.ok ? capabilitiesData.data : undefined;

  const tags = tagsData?.ok
    ? tagsData.data.filter((tag) => tag.slug !== "favorited")
    : [];

  const { languages, sources, audioSources, newsSources, socialSources } =
    React.useMemo(() => {
      const languages = capabilities?.languages || [];
      const sources = capabilities?.sources || [];
      const audioSources = capabilities?.sources_audio || [];
      const newsSources = capabilities?.sources_news || [];
      const socialSources = capabilities?.sources_social || [];

      return { languages, sources, audioSources, newsSources, socialSources };
    }, [capabilities]);

  const DEFAULT_QUERY: QueryIn = React.useMemo(() => {
    return {
      keywords: [],
      nearKeywords: [],
      notNearKeywords: [],
      notNearDistance: 10,
      nearDistance: 10,
      languages,
      sources,
      mentionTypes: MENTION_SOURCE_TYPE.ALL,
    };
  }, [languages, sources]);

  // Form state
  const form = useForm<QueryBuilderForm>({
    resolver: zodResolver(queryBuilderFormSchema),
    defaultValues: {
      title: "",
      type: SEARCH_TYPES.BRAND,
      startDate: startOfDay(subMonths(new Date(), 1)),
      endDate: undefined,
      queries: [],
    },
  });

  const startDate = useWatch({
    control: form.control,
    name: "startDate",
  });

  const endDate = useWatch({
    control: form.control,
    name: "endDate",
  });

  const queriesField = useFieldArray({
    control: form.control,
    name: "queries",
  });

  const queries = useWatch({
    control: form.control,
    name: "queries",
  });

  // Side-effects
  React.useEffect(() => {
    if (capabilities && !hasInitializedForm.current) {
      hasInitializedForm.current = true;
      queriesField.append(DEFAULT_QUERY);
    }
  }, [DEFAULT_QUERY, capabilities, queriesField]);

  // Helpers
  function resetQueries() {
    form.setValue("queries", []);
  }

  // Handlers
  function handleAddEndDate() {
    const startDate = form.getValues("startDate");
    const today = new Date();
    const newEndDate = endOfDay(addMonths(startDate || today, 1));
    form.setValue("endDate", newEndDate);
  }

  function handleChangeStartDate(date?: Date) {
    if (!date) {
      return;
    }

    form.setValue("startDate", startOfDay(date));
  }

  function handleChangeEndDate(date?: Date) {
    if (date) {
      form.setValue("endDate", endOfDay(date));
    } else {
      form.setValue("endDate", undefined);
    }
  }

  function handleDisableEndDate(date: Date) {
    if (startDate) {
      return isBefore(date, startOfDay(startDate));
    }

    return false;
  }

  function handleChangeType(type: SearchTypes) {
    form.setValue("type", type);
  }

  function handleApplySearchTemplate(
    queries: Array<Omit<SearchQuery, "uuid"> & { uuid?: string | null }>
  ) {
    resetQueries();

    const camelCaseQueryKeys = Object.keys(PROP_TO_PARAM_MAP) as FilterKey[];

    const camelCaseQueries: QueryIn[] = queries.map((query) => {
      const q = camelCaseQueryKeys.reduce(
        (all, each) => {
          all[each] = query[PROP_TO_PARAM_MAP[each]];
          return all;
        },
        {} as Record<FilterKey, unknown>
      );

      return q as QueryIn;
    });

    camelCaseQueries.forEach((query) => {
      queriesField.append(query);
    });
  }

  async function handleCopyQueries() {
    try {
      const preparedQueries = queries.map(prepareQueryForCreate);
      const jsonString = JSON.stringify({ queries: preparedQueries }, null, 2);
      await navigator.clipboard.writeText(jsonString);
      toast.success(t`JSON kopierades till urklipp`);
    } catch {
      toast.error(t`Kunde inte kopiera JSON`);
    }
  }

  const handleSubmit: SubmitHandler<QueryBuilderForm> = function (values, e) {
    const nativeEvent = e?.nativeEvent as SubmitEvent;
    const submitter = nativeEvent?.submitter as HTMLButtonElement;
    const submitterName = submitter?.name as QueryBuilderSubmitButton;
    onSubmit(values, submitterName);
  };

  return (
    <div className="flex flex-col gap-6">
      <AccessControl level={ACCESS_LEVELS.STAFF}>
        <Staff
          onApplySearchQueries={handleApplySearchTemplate}
          onCopyQueries={handleCopyQueries}
        />
      </AccessControl>

      <form
        id="form-qb"
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-6"
      >
        <Card>
          <CardHeader>
            <CardTitle>
              <Trans>Basinformation</Trans>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FieldGroup>
              <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                <Controller
                  name="title"
                  control={form.control}
                  render={({ field, fieldState }) => {
                    const id = "form-qb-title";

                    return (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor={id}>
                          <Trans>Titel</Trans>
                        </FieldLabel>
                        <Input
                          {...field}
                          placeholder={t`Ge din bevakning en tydlig och beskrivande titel.`}
                          id={id}
                          aria-invalid={fieldState.invalid}
                          autoComplete="off"
                        />
                        {fieldState.invalid && (
                          <FieldError errors={[fieldState.error]} />
                        )}
                      </Field>
                    );
                  }}
                />

                <Controller
                  name="type"
                  control={form.control}
                  render={({ field, fieldState }) => {
                    const id = "form-qb-type";

                    return (
                      <Field>
                        <FieldLabel htmlFor={id}>
                          <Trans>Söktyp</Trans>
                        </FieldLabel>
                        <Select
                          value={field.value}
                          onValueChange={(newType) => {
                            handleChangeType(newType as SearchTypes);
                          }}
                        >
                          <SelectTrigger
                            id={id}
                            aria-invalid={fieldState.invalid}
                          >
                            <SelectValue placeholder={t`Välj vad du bevakar`} />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(SEARCH_TYPE_TO_LABEL).map(
                              ([value, label]) => (
                                <SelectItem
                                  id={`form-qb-type-${value}`}
                                  key={value}
                                  value={value}
                                >
                                  {i18n._(label)}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                        {fieldState.invalid && (
                          <FieldError errors={[fieldState.error]} />
                        )}
                      </Field>
                    );
                  }}
                />

                <Controller
                  name="startDate"
                  control={form.control}
                  render={({ field, fieldState }) => {
                    const id = "form-qb-start-date";

                    return (
                      <Field>
                        <FieldLabel htmlFor={id}>
                          <Trans>Startdatum</Trans>
                        </FieldLabel>
                        <DatePicker
                          value={field.value}
                          id={id}
                          aria-invalid={fieldState.invalid}
                          onChangeDate={handleChangeStartDate}
                        />
                        {fieldState.invalid && (
                          <FieldError errors={[fieldState.error]} />
                        )}
                      </Field>
                    );
                  }}
                />

                {endDate ? (
                  <Controller
                    name="endDate"
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const id = "form-qb-end-date";

                      return (
                        <Field>
                          <FieldLabel htmlFor={id}>
                            <Trans>Slutdatum</Trans>
                          </FieldLabel>
                          <DatePicker
                            id={id}
                            aria-invalid={fieldState.invalid}
                            isClearable
                            disabled={handleDisableEndDate}
                            clearLabel={t`Ta bort slutdatum`}
                            value={field.value}
                            onChangeDate={(newEndDate) => {
                              handleChangeEndDate(newEndDate);
                            }}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      );
                    }}
                  />
                ) : (
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="link"
                      className="-mx-3"
                      onClick={handleAddEndDate}
                    >
                      <PlusIcon />
                      <Trans>Lägg till slutdatum</Trans>
                    </Button>
                  </div>
                )}
              </div>
            </FieldGroup>
          </CardContent>
        </Card>

        {queriesField.fields.map((query, queryIndex) => (
          <Query
            socialSources={socialSources}
            defaultQuery={DEFAULT_QUERY}
            queriesField={queriesField}
            audioSources={audioSources}
            newsSources={newsSources}
            languages={languages}
            index={queryIndex}
            queries={queries}
            sources={sources}
            key={query.id}
            form={form}
            tags={tags}
          />
        ))}
      </form>
    </div>
  );
}
